import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X, ShieldCheck, KeyRound } from "lucide-react";
import { adminClient } from "./client";

export function BLAdmins({ me, onChange }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminClient.get("/blackleaf/admin/admins");
      setAdmins(r.data.items);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const removeAdmin = async (id) => {
    if (!window.confirm("Remove this admin?")) return;
    try {
      await adminClient.delete(`/blackleaf/admin/admins/${id}`);
      toast.success("Admin removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove");
    }
  };

  return (
    <section className="bl-section" data-testid="bl-admins">
      <header className="bl-section-head">
        <div>
          <p className="bl-kicker">Society admins</p>
          <h2 className="bl-h2">Who can approve & moderate</h2>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setChangingPw(true)} className="bl-btn-ghost" data-testid="bl-change-password"><KeyRound size={12}/> Change my password</button>
          <button onClick={() => setInviting(true)} className="bl-btn" data-testid="bl-invite-admin"><Plus size={14}/> Invite an admin</button>
        </div>
      </header>

      {loading ? <Loader2 size={20} className="animate-spin mx-auto block text-bl-soft my-10"/> :
        <div className="space-y-3">
          {admins.map((a) => (
            <article key={a.id} className="bl-card p-4 flex items-center gap-4" data-testid={`bl-admin-${a.id}`}>
              <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "rgba(217,179,106,0.14)", border: "1px solid var(--bl-gold-deep)" }}>
                <ShieldCheck size={20} style={{ color: "var(--bl-gold)" }}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <strong style={{ color: "var(--bl-ink)", fontFamily: "Playfair Display, serif" }}>{a.name || "—"}</strong>
                  {a.id === me?.id && <span className="bl-badge">You</span>}
                </div>
                <p className="text-sm text-bl-soft truncate">{a.email}</p>
                <p className="text-xs text-bl-soft italic mt-0.5">Since {new Date(a.created_at).toLocaleDateString()} · invited by {a.created_by || "—"}</p>
              </div>
              {a.id !== me?.id && (
                <button onClick={() => removeAdmin(a.id)} className="bl-btn-ghost" data-testid={`bl-admin-del-${a.id}`}><Trash2 size={12}/></button>
              )}
            </article>
          ))}
        </div>
      }

      {inviting && <InviteAdmin onClose={() => setInviting(false)} onSaved={() => { setInviting(false); load(); onChange && onChange(); }}/>}
      {changingPw && <ChangePassword onClose={() => setChangingPw(false)}/>}
    </section>
  );
}

function InviteAdmin({ onClose, onSaved }) {
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await adminClient.post("/blackleaf/admin/admins", form);
      toast.success("Admin invited");
      onSaved();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Couldn't invite");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/75" onClick={onClose}/>
      <div className="bl-card relative w-full max-w-md p-6 sm:p-8 text-left">
        <button onClick={onClose} className="absolute top-3 right-3 bl-btn-ghost"><X size={12}/></button>
        <div className="bl-kicker mb-1">Invite an admin</div>
        <h3 className="bl-h2 mb-4">Add another moderator</h3>
        <p className="text-xs text-bl-soft italic mb-4">They'll get full moderation access — pick who you trust.</p>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Name"><input className="bl-field mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="bl-invite-name"/></Field>
          <Field label="Email *"><input type="email" className="bl-field mt-1.5" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="bl-invite-email"/></Field>
          <Field label="Temporary password *"><input type="password" className="bl-field mt-1.5" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={8} placeholder="min 8 characters" data-testid="bl-invite-password"/></Field>
          {err && <div className="text-sm text-bl-rose bg-bl-rose/10 px-3 py-2 rounded-lg border border-bl-rose/30">{err}</div>}
          <button type="submit" className="bl-btn w-full" disabled={busy} data-testid="bl-invite-submit">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>} Invite admin
          </button>
        </form>
      </div>
    </div>
  );
}

function ChangePassword({ onClose }) {
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await adminClient.post("/blackleaf/admin/change-password", form);
      toast.success("Password updated");
      onClose();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Couldn't change");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/75" onClick={onClose}/>
      <div className="bl-card relative w-full max-w-md p-6 sm:p-8 text-left">
        <button onClick={onClose} className="absolute top-3 right-3 bl-btn-ghost"><X size={12}/></button>
        <div className="bl-kicker mb-1">Your account</div>
        <h3 className="bl-h2 mb-4">Change password</h3>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Current password"><input type="password" className="bl-field mt-1.5" value={form.current_password} onChange={(e) => setForm((f) => ({ ...f, current_password: e.target.value }))} required data-testid="bl-cp-current"/></Field>
          <Field label="New password (min 8)"><input type="password" className="bl-field mt-1.5" value={form.new_password} onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))} required minLength={8} data-testid="bl-cp-new"/></Field>
          {err && <div className="text-sm text-bl-rose bg-bl-rose/10 px-3 py-2 rounded-lg border border-bl-rose/30">{err}</div>}
          <button type="submit" className="bl-btn w-full" disabled={busy} data-testid="bl-cp-submit">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <KeyRound size={14}/>} Update password
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-bl-soft">{label}</span>
      {children}
    </label>
  );
}
